import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { apiMessage } from "../apiMessage";
import { AuthLayout } from "../components/AuthLayout";
import { roleHome } from "../../../utils/roleHome";

const portals = {
  admin: {
    roles: ["admin", "superadmin", "staff"],
    eyebrow: "Admin console",
    title: "Admin sign in",
    subtitle: "Sign in with your staff credentials to manage operations.",
    tagline: { title: "Run the center from one console.", body: "Programs, assessments, scheduling, and members — all in one place." },
    submitLabel: "Enter admin panel",
    deniedMessage: "This account does not have admin or staff access",
  },
  coach: {
    roles: ["coach"],
    eyebrow: "Coach portal",
    title: "Coach sign in",
    subtitle: "Sign in to manage your sessions, rosters, and player assessments.",
    tagline: { title: "Coach with clarity.", body: "Your sessions, your rosters, your player development — in one place." },
    submitLabel: "Enter coach portal",
    deniedMessage: "This account does not have coach access",
  },
  parent: {
    roles: ["parent"],
    eyebrow: "Parent account",
    title: "Welcome back",
    subtitle: "Sign in to track your players' progress and manage bookings.",
    tagline: undefined,
    submitLabel: "Sign in",
    deniedMessage: "This account is not a parent account",
  },
};

export function LoginPage({ portal = "parent" }) {
  const config = portals[portal] || portals.parent;
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (auth.isAuthenticated) return <Navigate to={roleHome(auth.user?.role)} replace />;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const response = await api.post("/auth/login", form);
      const data = response.data;
      if (!config.roles.includes(data.user?.role)) {
        throw new Error(config.deniedMessage);
      }
      auth.signIn(data);
      const requested = location.state?.from;
      const safeDestination = typeof requested === "string" && requested.startsWith("/") && !requested.startsWith("//") ? requested : roleHome(data.user.role);
      navigate(safeDestination, { replace: true });
    } catch (err) {
      setError(apiMessage(err, "Unable to sign in"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      eyebrow={config.eyebrow}
      title={config.title}
      subtitle={config.subtitle}
      tagline={config.tagline}
    >
      <form onSubmit={submit} className="auth-form">
        {location.state?.notice && <div className="ops-alert ops-alert--success" role="status">{location.state.notice}</div>}
        <div>
          <label className="ops-form-label">Email address</label>
          <input
            required
            type="email"
            className="ops-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>

        <div>
          <label className="ops-form-label">Password</label>
          <input
            required
            type="password"
            className="ops-input"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </div>

        {error && <div className="ops-alert ops-alert--error">{error}</div>}

        <button type="submit" className="ops-button ops-button--primary auth-submit" disabled={submitting}>
          {submitting ? "Signing in…" : config.submitLabel}
        </button>
      </form>

      <div className="auth-footer">
        <Link to="/forgot-password">Forgot password?</Link>
        {portal === "parent" && <Link to="/register">Create an account</Link>}
      </div>
    </AuthLayout>
  );
}
